import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getMenu, createOrder } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { formatCurrency } from '../../utils/formatCurrency';
import ItemConfigModal from './ItemConfigModal';
import './WaiterOrderModal.css';

function WaiterOrderModal({ restaurantId, tableNumber, sessionId, onClose, onOrderCreated }) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [menu, setMenu] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [cartItems, setCartItems] = useState([]);
  const [configItem, setConfigItem] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [cartExpanded, setCartExpanded] = useState(false);

  // Escape key handler — only when no child modal (ItemConfigModal) is open
  useEffect(() => {
    if (configItem) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [configItem, onClose]);

  useEffect(() => {
    let cancelled = false;
    const fetchMenu = async () => {
      try {
        setLoading(true);
        const data = await getMenu(restaurantId);
        if (!cancelled) {
          setMenu(data.menu || data);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Error fetching menu:', err);
          setError(t('waiterOrder.menuLoadError'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchMenu();
    return () => { cancelled = true; };
  }, [restaurantId, t]);

  // Extract categories with available items
  const categories = useMemo(() => {
    if (!menu || !Array.isArray(menu)) return [];
    return menu.filter((cat) =>
      cat.items?.some((item) => item.isAvailable !== false)
    );
  }, [menu]);

  // Filtered items based on search or selected category
  const filteredItems = useMemo(() => {
    if (!categories.length) return [];

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const results = [];
      categories.forEach((cat) => {
        cat.items?.forEach((item) => {
          if (item.isAvailable !== false && item.name.toLowerCase().includes(query)) {
            results.push(item);
          }
        });
      });
      return results;
    }

    if (selectedCategory === null) {
      // Show all
      const results = [];
      categories.forEach((cat) => {
        cat.items?.forEach((item) => {
          if (item.isAvailable !== false) results.push(item);
        });
      });
      return results;
    }

    const cat = categories[selectedCategory];
    return cat?.items?.filter((item) => item.isAvailable !== false) || [];
  }, [categories, selectedCategory, searchQuery]);

  // Cart helpers
  const cartKey = (item) => {
    const modKey = item.selectedModifiers
      ? item.selectedModifiers.map((m) => m.optionId).sort().join(',')
      : '';
    return `${item.itemId || item.id}__${modKey}`;
  };

  const cartCount = cartItems.reduce((sum, ci) => sum + ci.quantity, 0);
  const cartTotal = cartItems.reduce((sum, ci) => sum + ci.price * ci.quantity, 0);

  const getItemCartQty = useCallback((item) => {
    return cartItems
      .filter((ci) => (ci.itemId || ci.id) === (item.itemId || item.id))
      .reduce((sum, ci) => sum + ci.quantity, 0);
  }, [cartItems]);

  const handleItemTap = (item) => {
    const hasModifiers = item.modifierGroups?.length > 0;
    if (hasModifiers) {
      setConfigItem(item);
      return;
    }
    // Quick-add: no modifiers
    addToCart({
      ...item,
      quantity: 1,
      selectedModifiers: [],
      unitPrice: item.price,
      price: item.price,
    });
  };

  const addToCart = (cartItem) => {
    setCartItems((prev) => {
      const key = cartKey(cartItem);
      const existingIndex = prev.findIndex((ci) => cartKey(ci) === key);
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          quantity: updated[existingIndex].quantity + cartItem.quantity,
        };
        return updated;
      }
      return [...prev, cartItem];
    });
  };

  const updateCartQty = (index, delta) => {
    setCartItems((prev) => {
      const updated = [...prev];
      const newQty = updated[index].quantity + delta;
      if (newQty <= 0) {
        updated.splice(index, 1);
      } else {
        updated[index] = { ...updated[index], quantity: newQty };
      }
      return updated;
    });
  };

  const removeCartItem = (index) => {
    setCartItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleConfigAdd = (configuredItem) => {
    addToCart(configuredItem);
    setConfigItem(null);
  };

  const handleSubmit = async () => {
    if (cartItems.length === 0 || submitting) return;
    setSubmitting(true);
    try {
      const items = cartItems.map((ci) => ({
        itemId: ci.itemId || ci.id,
        name: ci.name,
        quantity: ci.quantity,
        price: ci.price,
        unitPrice: ci.unitPrice || ci.price,
        selectedModifiers: ci.selectedModifiers || [],
        notes: ci.notes,
      }));

      await createOrder({
        restaurantId,
        tableNumber,
        sessionId,
        orderType: 'dine_in',
        items,
      });

      setShowSuccess(true);
      setTimeout(() => {
        onOrderCreated();
        onClose();
      }, 1200);
    } catch (err) {
      console.error('Error creating order:', err);
      showToast(t('waiterOrder.createError'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (showSuccess) {
    return (
      <div className="waiter-order-overlay">
        <div className="waiter-order-success">
          <span className="waiter-order-success-icon">✓</span>
          <span className="waiter-order-success-text">{t('waiterOrder.createSuccess')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="waiter-order-overlay">
      {/* Header */}
      <div className="waiter-order-header">
        <button className="waiter-order-close" onClick={onClose}>✕</button>
        <div className="waiter-order-header-info">
          <span className="waiter-order-title">{t('waiterOrder.title')}</span>
          <span className="waiter-order-table">{t('waiterOrder.tableTitle', { table: tableNumber })}</span>
        </div>
        {cartItems.length > 0 && (
          <button className="waiter-order-clear" onClick={() => setCartItems([])}>
            {t('waiterOrder.clearCart')}
          </button>
        )}
      </div>

      {/* Search */}
      <div className="waiter-order-search">
        <input
          type="text"
          className="waiter-order-search-input"
          placeholder={t('waiterOrder.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            if (e.target.value) setSelectedCategory(null);
          }}
        />
        {searchQuery && (
          <button className="waiter-order-search-clear" onClick={() => setSearchQuery('')}>
            ✕
          </button>
        )}
      </div>

      {/* Categories */}
      {!searchQuery && (
        <div className="waiter-order-categories">
          <button
            className={`waiter-order-cat-tab ${selectedCategory === null ? 'cat-active' : ''}`}
            onClick={() => setSelectedCategory(null)}
          >
            {t('waiterOrder.allCategories')}
          </button>
          {categories.map((cat, idx) => (
            <button
              key={cat.categoryId || idx}
              className={`waiter-order-cat-tab ${selectedCategory === idx ? 'cat-active' : ''}`}
              onClick={() => setSelectedCategory(idx)}
            >
              {cat.name}
            </button>
          ))}
        </div>
      )}

      {/* Content */}
      <div className="waiter-order-content">
        {loading && (
          <div className="waiter-order-loading">{t('common.loading')}</div>
        )}
        {error && (
          <div className="waiter-order-error">{error}</div>
        )}
        {!loading && !error && filteredItems.length === 0 && (
          <div className="waiter-order-empty">{t('orders.noOrders')}</div>
        )}
        {!loading && !error && (
          <div className="waiter-order-items">
            {filteredItems.map((item) => {
              const qty = getItemCartQty(item);
              const hasModifiers = item.modifierGroups?.length > 0;
              return (
                <button
                  key={item.itemId || item.id}
                  className={`waiter-order-item-card ${hasModifiers ? 'has-modifiers' : ''}`}
                  onClick={() => handleItemTap(item)}
                >
                  <div className="waiter-order-item-info">
                    <span className="waiter-order-item-name">{item.name}</span>
                    <span className="waiter-order-item-price">
                      {formatCurrency(item.price)}
                    </span>
                  </div>
                  {qty > 0 && (
                    <span className="waiter-order-item-badge">{qty}</span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Cart bar */}
      <div className={`waiter-order-cart-bar ${cartItems.length === 0 ? 'cart-empty' : ''}`}>
        {cartItems.length === 0 ? (
          <span className="waiter-order-cart-hint">{t('waiterOrder.emptyCart')}</span>
        ) : (
          <>
            {cartExpanded && (
              <div className="waiter-order-cart-expanded">
                {cartItems.map((ci, idx) => (
                  <div key={idx} className="waiter-order-cart-item">
                    <div className="waiter-order-cart-item-info">
                      <span className="waiter-order-cart-item-name">{ci.name}</span>
                      {ci.selectedModifiers?.length > 0 && (
                        <span className="waiter-order-cart-item-mods">
                          {ci.selectedModifiers.map((m) => m.optionName).join(', ')}
                        </span>
                      )}
                      {ci.notes && (
                        <span className="waiter-order-cart-item-notes">{ci.notes}</span>
                      )}
                    </div>
                    <div className="waiter-order-cart-item-controls">
                      <button className="cart-qty-btn" onClick={() => updateCartQty(idx, -1)}>−</button>
                      <span className="cart-qty-val">{ci.quantity}</span>
                      <button className="cart-qty-btn" onClick={() => updateCartQty(idx, 1)}>+</button>
                      <span className="cart-item-price">
                        {formatCurrency(ci.price * ci.quantity)}
                      </span>
                      <button className="cart-remove-btn" onClick={() => removeCartItem(idx)}>✕</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="waiter-order-cart-summary" onClick={() => setCartExpanded(!cartExpanded)}>
              <span className="waiter-order-cart-count">
                {t('waiterOrder.itemCount', { count: cartCount })}
              </span>
              <span className="waiter-order-cart-toggle">
                {cartExpanded ? '▾' : '▴'}
              </span>
              <span className="waiter-order-cart-total">
                {formatCurrency(cartTotal)}
              </span>
            </div>
            <button
              className="waiter-order-confirm-btn"
              onClick={handleSubmit}
              disabled={submitting || cartItems.length === 0}
            >
              {submitting ? t('waiterOrder.submitting') : t('waiterOrder.confirm')}
            </button>
          </>
        )}
      </div>

      {/* Item config sub-modal */}
      {configItem && (
        <ItemConfigModal
          item={configItem}
          onAdd={handleConfigAdd}
          onClose={() => setConfigItem(null)}
        />
      )}
    </div>
  );
}

export default WaiterOrderModal;
